import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useScript } from '../context/ScriptContext';
import { useAuth } from '../context/AuthContext';
import { CategoryWithChildren } from '../types';
import * as api from '../services/api';
import { ApiError } from '../services/api';
import ImageUpload from '../components/ImageUpload';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const CategoryEditPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useScript();
  const { isAdmin, token } = useAuth();
  const navigate = useNavigate();

  const [category, setCategory] = useState<CategoryWithChildren | null>(null);
  const [title, setTitle] = useState('');
  const [slugValue, setSlugValue] = useState('');
  const [description, setDescription] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useDocumentTitle(
    category ? `${t('Уреди')} — ${category.title}` : t('Уређивање категорије'),
    `/${slug}/uredi`
  );

  const loadCategory = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    api.getCategoryBySlug(slug)
      .then(data => {
        setCategory(data);
        setTitle(data.title);
        setSlugValue(data.slug);
        setDescription(data.description);
        setCoverImageUrl(data.coverImageUrl);
        setLoading(false);
      })
      .catch((e: ApiError) => {
        setError(e?.message || 'Категорија није пронађена.');
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => { loadCategory(); }, [loadCategory]);

  const handleSave = async () => {
    if (!token || !category) return;
    setSaving(true);
    try {
      const updatedSlug = slugValue.trim() || slug;
      await api.updateCategory(token, category.id, {
        title,
        description,
        coverImageUrl,
        slug: updatedSlug,
      });
      // Navigate to the (potentially new) slug
      navigate(`/${updatedSlug}`);
    } catch {
      setSaving(false);
    }
  };

  // Auto-generate SEO-friendly slug from title (only if slug hasn't been manually changed)
  const generateSlug = (text: string): string => {
    const map: Record<string, string> = {
      'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'ђ': 'dj', 'е': 'e',
      'ж': 'z', 'з': 'z', 'и': 'i', 'ј': 'j', 'к': 'k', 'л': 'l', 'љ': 'lj',
      'м': 'm', 'н': 'n', 'њ': 'nj', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's',
      'т': 't', 'ћ': 'c', 'у': 'u', 'ф': 'f', 'х': 'h', 'ц': 'c', 'ч': 'c',
      'џ': 'dz', 'ш': 's',
    };
    return text.toLowerCase()
      .split('')
      .map(ch => map[ch] || ch)
      .join('')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 80);
  };

  if (!isAdmin) {
    return (
      <div className="category-edit-page">
        <div className="state-block state-error" role="alert">
          <span>{t('Немате дозволу за приступ овој страници.')}</span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="category-edit-page">
        <div className="state-block" role="status" aria-live="polite">
          <div className="state-spinner" aria-hidden="true" />
          <span>{t('Учитавање...')}</span>
        </div>
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="category-edit-page">
        <div className="state-block state-error" role="alert">
          <span>{t(error || 'Категорија није пронађена.')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="category-edit-page">
      <h1 className="news-heading">{t('Уређивање категорије')}: {t(category.title)}</h1>

      <div className="category-edit-form">
        {/* Title */}
        <div className="form-group">
          <label className="post-admin-label">{t('Наслов')}</label>
          <p className="form-hint">{t('Наслов се приказује на страници и у претрази (SEO title). Нека буде јасан и описан.')}</p>
          <input
            type="text"
            className="post-admin-input"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        </div>

        {/* Slug / URL */}
        <div className="form-group">
          <label className="post-admin-label">{t('URL адреса (slug)')}</label>
          <p className="form-hint">
            {t('Линк на који води ова страница. Нпр.')}{' '}
            <code style={{ color: 'var(--gold-solid)' }}>/{slugValue}</code>
            {' — '}{t('Можете слободно мењати наслов а да URL остане исти.')}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: 'rgba(100,80,55,0.6)', fontFamily: 'monospace' }}>/</span>
            <input
              type="text"
              className="post-admin-input"
              value={slugValue}
              onChange={e => setSlugValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              style={{ fontFamily: 'monospace', flex: 1 }}
            />
          </div>
          <button
            type="button"
            className="btn-secondary"
            style={{ marginTop: '6px', fontSize: '0.75rem', padding: '4px 10px' }}
            onClick={() => setSlugValue(generateSlug(title))}
          >
            {t('Генериши из наслова')} ↻
          </button>
        </div>

        {/* Cover image */}
        <ImageUpload
          label={t('Насловна слика (cover)')}
          imageUrl={coverImageUrl}
          onImageUrlChange={setCoverImageUrl}
        />

        {/* Description / Content */}
        <div className="form-group">
          <label className="post-admin-label">{t('Опис / Текст')}</label>
          <p className="form-hint">
            {t('Користите **текст** за подебљан текст. Нови ред = Enter.')}<br />
            {t('Прва реченица је најважнија за SEO — опишите страницу јасно и привлачно.')}
          </p>
          <textarea
            rows={15}
            className="post-admin-textarea"
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </div>

        {/* Preview */}
        {description && (
          <div className="form-group">
            <label className="post-admin-label">{t('Преглед текста')}</label>
            <div className="category-description" style={{ background: 'var(--cream)', padding: '1.5rem', borderRadius: '12px' }}>
              <p>{renderPreview(description)}</p>
            </div>
          </div>
        )}

        {/* SEO Preview */}
        <div className="form-group">
          <label className="post-admin-label">{t('SEO преглед (Google)')}</label>
          <div style={{
            background: '#fff',
            border: '1px solid #dfe1e5',
            borderRadius: '8px',
            padding: '16px 18px',
            fontFamily: 'Arial, sans-serif',
          }}>
            <div style={{ color: '#1a0dab', fontSize: '18px', lineHeight: 1.3, marginBottom: '3px' }}>
              {title || t('Наслов странице')} — Манастир Градац
            </div>
            <div style={{ color: '#006621', fontSize: '13px', marginBottom: '3px' }}>
              manastirgradac.rs/{slugValue}
            </div>
            <div style={{ color: '#545454', fontSize: '13px', lineHeight: 1.5 }}>
              {description ? description.split('\n')[0].replace(/\*\*/g, '').slice(0, 155) + (description.length > 155 ? '...' : '') : t('Опис ће се приказати овде...')}
            </div>
          </div>
        </div>

        <div className="post-admin-actions">
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? t('Чување...') : t('Сачувај')}
          </button>
          <button className="btn-secondary" onClick={() => navigate(`/${slug}`)}>
            {t('Откажи')}
          </button>
        </div>
      </div>
    </div>
  );
};

function renderPreview(text: string) {
  const lines = text.split('\n');
  return lines.map((line, i) => {
    const parts = line.split(/\*\*(.*?)\*\*/g);
    return (
      <React.Fragment key={i}>
        {parts.map((part, j) =>
          j % 2 === 1 ? <strong key={j}>{part}</strong> : <span key={j}>{part}</span>
        )}
        {i < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
}

export default CategoryEditPage;
